import { useCallback, useMemo, useState } from 'react';
import { SuspenseWithSentryErrorBoundary } from '@retail/components';
import { SupplierRow, SuppliersTable, SuppliersTableToolbar } from '@retail/suppliers/components';
import { NotificationOption, notificationOptionToDtoMap, Supplier } from '@retail/suppliers/types';
import { useFetchAllSuppliers } from '@retail/products/data-access';
import { useDisclosure, useSelectedSearchParamState } from '@shared/hooks';
import { SupplierAgreementsDialogContainer } from '@retail/suppliers/containers';
import { AgreementsUploadDialog } from '@retail/purchase-agreements/containers';
import { useLanguageSelector } from '@retail/app/hooks';
import {
  useIsFeatureEnabled,
  usePermissionsForCurrentUser,
  useSelectedOrgUnit,
} from '@retail/app/stores/selected-context';
import { GridDensity } from '@mui/x-data-grid';
import {
  useDeleteNonStockSupplier,
  useMutateSupplierConfig,
  useSetNonStockSupplier,
} from '@retail/suppliers/data-access';
import { useToast } from '@retail/hooks';
import { useQueryClient } from '@tanstack/react-query';
import { useSuppliersTFunction } from '@retail/suppliers/i18n';
import { CreateSupplierLevelAgreementConditionsDialog } from '@retail/calculus/containers';

export function SuppliersPage() {
  const { language } = useLanguageSelector();
  const toast = useToast();
  const queryClient = useQueryClient();
  const t = useSuppliersTFunction();
  const { assortmentId, appTenant } = useSelectedOrgUnit();

  const { isOpen: hasActiveAgreement, onToggle: hasActiveAgreementToggle } = useDisclosure();
  const isSupplierPriceChangeConfigurationEnabled = useIsFeatureEnabled(
    'supplier-price-change-notification-config'
  );

  const { userCanCreate, userCanUpdate } = usePermissionsForCurrentUser();
  const canUploadPurchasePrices = userCanCreate('PURCHASE_PRICES');
  const canActivateSupplierAgreements = userCanCreate('PRICE_CALCULATION__PURCHASE_CONDITION');
  const canUpdateNonStockSupplier = userCanUpdate('ASSORTMENT');

  const [searchInput, setSearchInput] = useState<string>('');

  const { mutateAsync: mutateSupplierConfig, isPending } = useMutateSupplierConfig({
    assortmentId,
    suspense: false,
  });

  const { mutateAsync: mutateDeleteNonStockSupplier } = useDeleteNonStockSupplier({ assortmentId });
  const { mutateAsync: mutateSetNonStockSupplier } = useSetNonStockSupplier({ assortmentId });

  const handleUpdateNonStockSupplier = useCallback(
    (supplier: Supplier) => {
      if (supplier.nonStockItemSupplier) {
        mutateDeleteNonStockSupplier({ urlVariables: supplier.mgSupplierId })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['suppliers'] });
            toast.success(
              t('suppliers.nonStockItemSupplier.isRemoved', {
                supplier: supplier.name,
              })
            );
          })
          .catch((e) => {
            console.error(e);
            toast.error(t('suppliers.nonStockItemSupplier.removalFailed'));
          });
      } else {
        mutateSetNonStockSupplier({ urlVariables: supplier.mgSupplierId })
          .then(() => {
            queryClient.invalidateQueries({ queryKey: ['suppliers'] });
            toast.success(
              t('suppliers.nonStockItemSupplier.isAdded', {
                supplier: supplier.name,
              })
            );
          })
          .catch((e) => {
            console.error(e);
            toast.error(t('suppliers.nonStockItemSupplier.addingFailed'));
          });
      }
    },
    [mutateDeleteNonStockSupplier, mutateSetNonStockSupplier, queryClient, t, toast]
  );

  const handleSupplierPriceChangeConfig = useCallback(
    (supplier: Supplier, notificationOption: NotificationOption) => {
      if ((supplier.config?.priceChangeNotificationConfig ?? 'ignore') === notificationOption) {
        return;
      }
      mutateSupplierConfig({
        urlVariables: supplier.mgSupplierId,
        body: {
          notificationConfig: [
            {
              type: 'PRICE_SUGGESTION',
              value: notificationOptionToDtoMap[notificationOption],
            },
          ],
        },
      })
        .then(() => {
          toast.success(
            t('suppliers.columns.priceChangeConfig.request.success', {
              supplierName: supplier.name,
            })
          );
          queryClient.invalidateQueries({ queryKey: ['suppliers'] });
        })
        .catch((err) => {
          console.error(err);
          toast.error(t('suppliers.columns.priceChangeConfig.request.error'));
        });
    },
    [mutateSupplierConfig, queryClient, t, toast]
  );

  const [gridDensity, setGridDensity] = useState<GridDensity>('standard');

  const { data: suppliersDtos = [] } = useFetchAllSuppliers({
    assortmentId,
    includeConfiguration: true,
  });
  const suppliers = useMemo(() => suppliersDtos.map(Supplier.fromDTO), [suppliersDtos]);
  const filteredSuppliers = useMemo(
    () =>
      suppliers.filter(({ name, mgSupplierId, finfoSupplierId, hasSelectedSupplierAgreement }) => {
        const supplierSearchFilter =
          name?.toLocaleLowerCase().startsWith(searchInput.toLocaleLowerCase()) ||
          (mgSupplierId + '').toLocaleLowerCase().startsWith(searchInput.toLocaleLowerCase()) ||
          (finfoSupplierId + '').toLocaleLowerCase().startsWith(searchInput.toLocaleLowerCase());
        if (hasActiveAgreement) {
          return supplierSearchFilter && !!hasSelectedSupplierAgreement;
        } else {
          return supplierSearchFilter;
        }
      }),
    [hasActiveAgreement, searchInput, suppliers]
  );

  const [selectedSupplierToView, setSelectedSupplierToView] = useSelectedSearchParamState({
    options: suppliers,
    getOptionId: ({ mgSupplierId }) => mgSupplierId,
    searchParamKey: 'supplier',
  });

  const [selectedSupplierToUploadAgreements, _setSelectedSupplierToUploadAgreements] =
    useSelectedSearchParamState({
      options: suppliers,
      getOptionId: ({ mgSupplierId }) => mgSupplierId,
      searchParamKey: 'upload-supplier',
    });
  const setSelectedSupplierToUploadAgreements = canUploadPurchasePrices
    ? _setSelectedSupplierToUploadAgreements
    : undefined;

  const { isOpen: isActivateAgreementsOpen, onToggle: toggleActivateAgreements } = useDisclosure();

  const supplierTableRows: SupplierRow[] = useMemo(
    () =>
      filteredSuppliers.map((supplier) => ({
        ...supplier,
        onEditPriceChangeConfig: isSupplierPriceChangeConfigurationEnabled
          ? (priceChangeConfig: NotificationOption) =>
              handleSupplierPriceChangeConfig(supplier, priceChangeConfig)
          : undefined,
      })),
    [filteredSuppliers, handleSupplierPriceChangeConfig, isSupplierPriceChangeConfigurationEnabled]
  );

  return (
    <>
      <SuspenseWithSentryErrorBoundary>
        <SuppliersTableToolbar
          searchInput={searchInput}
          onSearchChange={setSearchInput}
          gridDensity={gridDensity}
          setGridDensity={setGridDensity}
          hasActiveAgreement={hasActiveAgreement}
          hasActiveAgreementToggle={hasActiveAgreementToggle}
          openActivateAgreements={
            canActivateSupplierAgreements ? toggleActivateAgreements : undefined
          }
        />
        <SuppliersTable
          language={language}
          gridDensity={gridDensity}
          suppliers={supplierTableRows}
          onSupplierSelect={setSelectedSupplierToView}
          onShowUploadDialog={setSelectedSupplierToUploadAgreements}
          onUpdateNonStockSupplier={
            canUpdateNonStockSupplier ? handleUpdateNonStockSupplier : undefined
          }
          enablePriceChangeConfig={isSupplierPriceChangeConfigurationEnabled}
          isLoading={isPending}
          countryCode={appTenant.countryCode}
        />
      </SuspenseWithSentryErrorBoundary>

      {selectedSupplierToView && (
        <SupplierAgreementsDialogContainer
          assortmentId={assortmentId}
          supplier={selectedSupplierToView}
          onClose={() => setSelectedSupplierToView(null)}
          open
        />
      )}
      {selectedSupplierToUploadAgreements && setSelectedSupplierToUploadAgreements && (
        <AgreementsUploadDialog
          open
          onClose={() => setSelectedSupplierToUploadAgreements(null)}
          supplier={selectedSupplierToUploadAgreements}
        />
      )}
      {isActivateAgreementsOpen && (
        <CreateSupplierLevelAgreementConditionsDialog
          open
          suppliers={filteredSuppliers}
          onClose={toggleActivateAgreements}
        />
      )}
    </>
  );
}
